/**
 * TrackingProvider
 * Provides tracking context for content interactions
 */

import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
export interface TrackingConfig {
    enabled?: boolean;
    endpoint?: string;
    batchSize?: number;
    flushInterval?: number;
}

export interface TrackingEvent {
    type: string;
    contentId?: string;
    contentType?: string;
    timestamp: number;
    data?: Record<string, unknown>;
}

export interface TrackingContextValue {
    /** Track an event */
    track: (type: string, data?: Record<string, unknown>) => void;
    /** Track content view */
    trackView: (contentId: string, contentType: string) => void;
    /** Track answer */
    trackAnswer: (contentId: string, answer: unknown, isCorrect?: boolean) => void;
    /** Track completion */
    trackComplete: (contentId: string, score?: number, max?: number) => void;
    /** Get all tracked events */
    getEvents: () => TrackingEvent[];
    /** Clear events */
    clearEvents: () => void;
    /** Whether tracking is enabled */
    enabled: boolean;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

export interface TrackingProviderProps {
    children: React.ReactNode;
    config?: TrackingConfig;
    onTrack?: (event: TrackingEvent) => void;
}

export function TrackingProvider({
    children,
    config,
    onTrack,
}: TrackingProviderProps): React.ReactElement {
    const eventsRef = useRef<TrackingEvent[]>([]);
    const enabled = config?.enabled !== false;

    const track = useCallback(
        (type: string, data?: Record<string, unknown>) => {
            if (!enabled) return;

            const event: TrackingEvent = {
                type,
                timestamp: Date.now(),
                data,
            };

            eventsRef.current.push(event);
            onTrack?.(event);
        },
        [enabled, onTrack]
    );

    const trackView = useCallback(
        (contentId: string, contentType: string) => {
            track('view', { contentId, contentType });
        },
        [track]
    );

    const trackAnswer = useCallback(
        (contentId: string, answer: unknown, isCorrect?: boolean) => {
            track('answer', { contentId, answer, isCorrect });
        },
        [track]
    );

    const trackComplete = useCallback(
        (contentId: string, score?: number, max?: number) => {
            track('complete', { contentId, score, max });
        },
        [track]
    );

    const getEvents = useCallback(() => [...eventsRef.current], []);

    const clearEvents = useCallback(() => {
        eventsRef.current = [];
    }, []);

    const value = useMemo<TrackingContextValue>(
        () => ({
            track,
            trackView,
            trackAnswer,
            trackComplete,
            getEvents,
            clearEvents,
            enabled,
        }),
        [track, trackView, trackAnswer, trackComplete, getEvents, clearEvents, enabled]
    );

    return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
}

export function useTrackingContext(): TrackingContextValue {
    const context = useContext(TrackingContext);
    if (!context) {
        // Return a no-op tracker if not in provider
        return {
            track: () => {},
            trackView: () => {},
            trackAnswer: () => {},
            trackComplete: () => {},
            getEvents: () => [],
            clearEvents: () => {},
            enabled: false,
        };
    }
    return context;
}

